import { useState } from 'react';
import type { UpdateCourseProgressV2Context } from '@scale/genai-compass-shared/cards/update_course_progress_v2';
import { useCompassCard } from '@scale/compass-ui/store';
import { columnsFromRecord } from '@scale/compass-shared/utils';
import _ from 'lodash';

const useUpdateCourseProgressV2Card = (index: number) => {
  const { card, updateCardContext, updateCardCanSubmit, error } = useCompassCard(index);
  const { retrievedOutput } = useCompassCard(index - 1);
  const context = card.defaultContext as UpdateCourseProgressV2Context;

  // Get columns and data from previous card's output
  const columns = columnsFromRecord(retrievedOutput?.response?.data) || [];
  const data = retrievedOutput?.response?.data ?? [];

  const [numCourseProgresses, setNumCourseProgresses] = useState(0);

  const checkCanSubmit = (
    courseProgressColumn: string | undefined,
    statusColumn: string | undefined,
  ): boolean => {
    if (!courseProgressColumn || !statusColumn) return false;
    const hasValidCourseProgress = !_.isEmpty(courseProgressColumn);
    const hasValidStatusColumn = !_.isEmpty(statusColumn);
    const shouldTagUsers = context.shouldTagUsers;
    const hasValidTagIdsColumn = !_.isEmpty(context.tagIdsColumn);
    if (shouldTagUsers && !hasValidTagIdsColumn) return false;
    return hasValidCourseProgress && hasValidStatusColumn;
  };

  const setSelectedCourseProgressV2Column = (selectedCourseProgressV2Column: string) => {
    const columnId = selectedCourseProgressV2Column;

    updateCardContext({
      ...context,
      selectedCourseProgressV2Column: columnId,
    } as UpdateCourseProgressV2Context);

    if (!columnId) {
      setNumCourseProgresses(0);
      updateCardCanSubmit(false);
      return;
    }

    const progressRows = data.filter((row: any) => row[columnId]);
    setNumCourseProgresses(progressRows.length);

    updateCardCanSubmit(checkCanSubmit(columnId, context.selectedCourseProgressV2StatusColumn));
  };

  const setSelectedCourseProgressV2StatusColumn = (
    selectedCourseProgressV2StatusColumn: string,
  ) => {
    updateCardContext({
      ...context,
      selectedCourseProgressV2StatusColumn,
    } as UpdateCourseProgressV2Context);

    updateCardCanSubmit(
      checkCanSubmit(context.selectedCourseProgressV2Column, selectedCourseProgressV2StatusColumn),
    );
  };

  const setShouldTagUsers = (shouldTagUsers: boolean) => {
    updateCardContext({
      ...context,
      shouldTagUsers,
    } as UpdateCourseProgressV2Context);
  };

  const setMarkAsSkipped = (markAsSkipped: boolean) => {
    updateCardContext({
      ...context,
      markAsSkipped,
    } as UpdateCourseProgressV2Context);
  };

  const setTagIdsColumn = (tagIdsColumn: string) => {
    updateCardContext({
      ...context,
      tagIdsColumn,
    } as UpdateCourseProgressV2Context);
  };

  return {
    selectedCourseProgressV2Column: context.selectedCourseProgressV2Column,
    setSelectedCourseProgressV2Column,
    selectedCourseProgressV2StatusColumn: context.selectedCourseProgressV2StatusColumn,
    setSelectedCourseProgressV2StatusColumn,
    numCourseProgresses,
    columns,
    shouldTagUsers: context.shouldTagUsers,
    setShouldTagUsers,
    tagIdsColumn: context.tagIdsColumn,
    setTagIdsColumn,
    markAsSkipped: context.markAsSkipped,
    setMarkAsSkipped,
    hasError: !!error,
  };
};

export default useUpdateCourseProgressV2Card;
